import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, catchError, map, throwError } from 'rxjs';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';
import { HttpService } from 'src/app/core/services/api/http.service';
import { ValidationService } from 'src/app/core/services/validation.service';
import { Router, ActivatedRoute } from '@angular/router';
import { API_VERSIONS } from 'src/app/shared/consts/api-versions.const';

@Injectable({
  providedIn: 'root',
})
export class ThirdPartiesService {
  private thirdPartyForm!: FormGroup;
  private thirdPartyTypeForm!: FormGroup;
  private newLienholderTypeForm!: FormGroup;
  private thirdPartydialogOpenSubject: Subject<{
    action: 'add' | 'edit';
    id?: number;
  }> = new Subject<{ action: 'add' | 'edit'; id?: number }>();

  private thirdPartyTypedialogOpenSubject: Subject<{
    action: 'add' | 'edit';
    id?: number;
  }> = new Subject<{ action: 'add' | 'edit'; id?: number }>();

  updateThirdPartyAndSettings: Subject<{ state: string; place: string }> =
    new Subject();

  private openDialogSubject = new Subject<any>();
  openDialog$ = this.openDialogSubject.asObservable();

  notCustomerFields = ['name'];

  constructor(
    private _api: HttpService,
    private errHandle: ErrorHandlingService,
    private validationService: ValidationService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.initializeThirdPartyForm();
    this.initializeThirdPartyTypeForm();
    this.initializeNewLienholderTypeForm();
  }

  private initializeThirdPartyForm() {
    this.thirdPartyForm = new FormGroup({
      type: new FormControl('', [Validators.required]),
      // code: new FormControl('', [Validators.required]),
      internal_code: new FormControl(''),
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(60),
        Validators.pattern(this.validationService?.alphaPattern),
      ]),
      title: new FormControl(''),
      first_name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(60),
        Validators.pattern(this.validationService?.alphaPattern),
      ]),
      middle_name: new FormControl(''),
      last_name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(60),
        Validators.pattern(this.validationService?.alphaPattern),
      ]),
      email: new FormControl('', [
        Validators.email,
        Validators.pattern(this.validationService?.emailPattern),
      ]),

      phone: new FormControl('', [
        Validators.required,
        Validators.pattern(this.validationService.validPhoneUS),
      ]),
      web_url: new FormControl('', [
        Validators.pattern(this.validationService?.urlPattern),
      ]),
      status: new FormControl(''),
      formAddress: new FormGroup({
        street_address: new FormControl('', [Validators.required]),
        country: new FormControl('', [Validators.required]),
        state: new FormControl('', [Validators.required]),
        city: new FormControl('', [Validators.required]),
        zip_code: new FormControl('', [Validators.required]),
      }),
      customFieldsArr: new FormArray([]),
      customFieldsArrValue: new FormControl('', []),
      flagged: new FormControl(false, []),
      flag_notes: new FormControl('', []),
      service_advisor_id : new FormControl(''),
      repair_facility_id : new FormControl('')
    });

    this.thirdPartyForm.get('type')?.valueChanges.subscribe((value) => {
      this.notCustomerFields.forEach((field) => {
        if (value === 1) {
          this.removeValidationForControl(field);
        } else {
          this.addValidationForControl(field);
        }
      });
    });
  }

  removeValidationForControl(controlName: string) {
    const control = this.thirdPartyForm.get(controlName);
    if (!controlName) {
      return;
    }
    control?.removeValidators([Validators.required]);
    control?.updateValueAndValidity();
  }

  addValidationForControl(controlName: string) {
    const control = this.thirdPartyForm.get(controlName);
    if (!controlName) {
      return;
    }
    control?.addValidators([Validators.required]);
    control?.updateValueAndValidity();
  }

  private initializeThirdPartyTypeForm() {
    this.thirdPartyTypeForm = new FormGroup({
      // id: new FormControl(''),
      prefix: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(15),
      ]),
      description: new FormControl(''),
      type_name: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
      ]),
      isActive: new FormControl(false),
    });
  }

  private initializeNewLienholderTypeForm() {
    this.newLienholderTypeForm = new FormGroup({
      type: new FormControl('', [Validators.required]),
      // code: new FormControl('', [Validators.required]),
      lienholderName: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.pattern(this.validationService?.alphaPattern),
      ]),
      address: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
      ]),
      title: new FormControl('', [Validators.required]),
      first_name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.pattern(this.validationService?.alphaPattern),
      ]),
      middle_name: new FormControl(''),
      last_name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.pattern(this.validationService?.alphaPattern),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern(this.validationService?.emailPattern),
      ]),
      phone: new FormControl('', [Validators.required]),
      web_url: new FormControl('', [
        Validators.pattern(this.validationService?.urlPattern),
      ]),
      country: new FormControl(''),
      state: new FormControl(''),
      city: new FormControl(''),
      zip_code: new FormControl('', [
        Validators.pattern(this.validationService?.numerical),
      ]),
      street_address: new FormControl(''),
    });
  }

  // ThirdParty
  getThirdPartyForm() {
    return this.thirdPartyForm;
  }
  // Lienholder
  getNewLienholderTypeForm() {
    return this.newLienholderTypeForm;
  }
  openThirdPartyDialog(action: 'add' | 'edit', id?: number) {
    // Initialize the form based on the action ('add' or 'edit')
    this.thirdPartydialogOpenSubject.next({ action, id });
  }
  onThirdPartyDialogOpen(): Observable<{
    action: 'add' | 'edit';
    id?: number;
  }> {
    return this.thirdPartydialogOpenSubject.asObservable();
  }

  // ThirdPartyType
  getThirdPartyTypeForm() {
    return this.thirdPartyTypeForm;
  }

  openThirdPartyTypeDialog(action: 'add' | 'edit', id?: number) {
    // Initialize the form based on the action ('add' or 'edit')
    this.thirdPartyTypedialogOpenSubject.next({ action, id });
  }

  onThirdPartyTypeDialogOpen(): Observable<{
    action: 'add' | 'edit';
    id?: number;
  }> {
    return this.thirdPartyTypedialogOpenSubject.asObservable();
  }

  invokeUpdatedThirdPartyMethod(state: string, place: string) {
    // Get current query params and URL
    const currentQueryParams = { ...this.activatedRoute.snapshot.queryParams };
    const currentUrl = this.router.url.split('?')[0];

    // Emit the update event
    this.updateThirdPartyAndSettings.next({ state, place });

    // Force a re-route to the same URL with preserved query params
    // This ensures the component reloads even if the URL hasn't changed
    this.router.navigateByUrl(
      `${currentUrl}?${new URLSearchParams(currentQueryParams).toString()}`
    );
  }

  getUpdatedThirdPartyObservable(): Observable<{
    state: string;
    place: string;
  }> {
    return this.updateThirdPartyAndSettings.asObservable();
  }

  getTitles(): Observable<any> {
    return this._api.getReq(`/${API_VERSIONS.TITLES}/titles`).pipe(
      map((res) => res?.data), // Extract data from the response
      catchError((err) => {
        return throwError(() => this.errHandle.errorHandling(err));
      })
    );
  }

  openDialog(thirdPartyobj: any) {
    this.openDialogSubject.next(thirdPartyobj);
  }
}
